import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ApiService, S3Service} from '../base';
import {ItemCreatedResponse, S3MultipartUploadResponse, S3UploadResponse, SignedUrlResponse} from '../../entities';
import {ItemUpdatedResponse} from '../../entities';
import {HttpMethod} from '../../types';
import {MultipartUploadInitiateResponse, TranscriptionCredentialsResponse} from './entities';
import {TranscriptionJobResponse} from './entities';
import {CreateTranscription} from './entities';
import {TranscriptionCredentials} from './entities';
import {MultipartUploadResult} from './entities';
import {UpdateTranscription} from './entities/update-transcription';

@Injectable({
  providedIn: 'root'
})
export class TranscriptionRepository {


  constructor(private api: ApiService, private s3: S3Service) {
  }

  getSignedUrl(file: string, method: HttpMethod, expires: number): Observable<string> {
    return this.api.get<SignedUrlResponse>('/transcribe/generate-signed-url', {fileName: file, action: method, expires: expires})
      .pipe(map(response => response.value));
  }

  createTranscriptionRecord(newTranscription: CreateTranscription): Observable<number> {
    return this.api.post<ItemCreatedResponse>('/transcribe', newTranscription)
      .pipe(map(response => response.value));
  }

  updateTranscriptionRecord(transcriptionId: number, updatedTranscription: UpdateTranscription): Observable<boolean> {
    return this.api.put<ItemUpdatedResponse>(`/transcribe/${transcriptionId}`, updatedTranscription)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  getTranscriptionCredentials(): Observable<TranscriptionCredentials> {
    return this.api.get<TranscriptionCredentialsResponse>('/transcribe/get-streaming-credentials')
      .pipe(map(response => response.value));
  }

  /**
   * Start AWS Transcribe Medical job for audio file in S3
   *
   * @param mediaFileKey - S3 key of the audio file to transcribe
   * @param patientId - Optional patient ID for context
   * @param commentId - Optional comment ID for context
   * @returns Observable with the transcription job response
   */
  startTranscription(
    mediaFileKey: string,
    patientId?: number | null,
    commentId?: number | null
  ): Observable<TranscriptionJobResponse> {
    return this.api.post<{ value: string; status: string }>('/transcribe/batch/start', {
      mediaFileKey,
      patientId,
      commentId,
      languageCode: 'en-US',
      type: 'DICTATION',
      mediaSampleRateHertz: 48000,
      mediaFormat: 'webm'
    }).pipe(map(response => ({
      jobName: response.value,  // Backend returns job name in "value" field
      s3Key: mediaFileKey,      // Echo back the S3 key for convenience
      status: response.status,
      errorMessage: ''
    })));
  }

  /**
   * Initiate S3 multipart upload
   *
   * @param fileName - File name/path for S3 (e.g., "transcriptions/2025-01-23/file.webm")
   * @param contentType - Content type (e.g., "audio/webm")
   * @returns Observable with multipart upload initiate response
   */
  initiateMultipartUpload(fileName: string, contentType: string): Observable<MultipartUploadResult> {
    return this.api.post<MultipartUploadInitiateResponse>('/transcribe/multipart/initiate', { fileName, contentType })
      .pipe(map(response => response.value));
  }

  /**
   * Get signed URL for uploading a part in multipart upload
   *
   * @param key - S3 key for the file
   * @param uploadId - Multipart upload ID
   * @param partNumber - Part number (1-indexed)
   * @param expiresInSeconds - Expiration time in seconds for the signed URL
   * @returns Observable with signed URL for the part
   */
  getPartUploadUrl(key: string, uploadId: string, partNumber: number, expiresInSeconds: number): Observable<string> {
    return this.api.get<SignedUrlResponse>('/transcribe/multipart/part-url', {
      key,
      uploadId,
      partNumber,
      expiresInSeconds
    }).pipe(map(response => response.value));
  }

  /**
   * Complete S3 multipart upload
   *
   * @param key - S3 key for the file
   * @param uploadId - Multipart upload ID
   * @param parts - Array of completed parts with ETags
   * @returns Observable with completion status
   */
  completeMultipartUpload(key: string, uploadId: string, parts: Array<{partNumber: number; eTag: string}>): Observable<boolean> {
    return this.api.post<ItemUpdatedResponse>('/transcribe/multipart/complete', {
      key,
      uploadId,
      parts
    }).pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  /**
   * Abort S3 multipart upload
   *
   * @param key - S3 key for the file
   * @param uploadId - Multipart upload ID
   * @returns Observable with abort status
   */
  abortMultipartUpload(key: string, uploadId: string): Observable<boolean> {
    return this.api.post<ItemUpdatedResponse>('/transcribe/multipart/abort', {
      key,
      uploadId
    }).pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  /**
   * Upload file to S3 using presigned URL
   * Note: Cannot add custom headers to presigned URLs as it breaks the signature
   *
   * @param file - File to upload
   * @param url - Presigned S3 URL
   * @returns Observable with upload response
   */
  uploadFile(file: File, url: string): Observable<S3UploadResponse> {
    return this.s3.uploadFile(url, file);  // No custom headers for presigned URLs
  }

  uploadMultipartFile(blob: Blob, url: string): Observable<S3MultipartUploadResponse> {
    return this.s3.uploadMultipartFile(url, blob, {}, { 'Content-Type': 'audio/webm;codecs=opus' });
  }
}
